import { useMemo } from "react";
import { Bar, Legend, Tooltip } from "recharts";
import type { DateRange } from "../DateRangePicker";
import { useMonthWindow } from "../../hooks/useMonthWindow";
import SessionsPerMonthChartBase from "./SessionsPerMonthChartBase";

export interface EntityMonthlyData {
  entityId: number | string;
  entityName: string;
  color: string;
  sessions: { date: Date }[];
}

interface SessionsPerMonthComparisonChartProps {
  entities: EntityMonthlyData[];
  dateRange: DateRange;
}

const SessionsPerMonthComparisonChart = ({
  entities,
  dateRange,
}: SessionsPerMonthComparisonChartProps) => {
  const {
    viewEndDate,
    monthLabels,
    canGoNext,
    periodText,
    goToPrevMonth,
    goToNextMonth,
    goToPrevYear,
    goToNextYear,
  } = useMonthWindow(dateRange);

  const chartData = useMemo(() => {
    const months: Array<{
      month: number;
      year: number;
      label: string;
      [key: string]: number | string;
    }> = [];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() - i, 1);
      const dataPoint: {
        month: number;
        year: number;
        label: string;
        [key: string]: number | string;
      } = {
        month: date.getMonth(),
        year: date.getFullYear(),
        label: `${monthLabels[date.getMonth()].slice(0, 3)} ${date
          .getFullYear()
          .toString()
          .slice(-2)}`,
      };

      entities.forEach((entity) => {
        dataPoint[`entity_${entity.entityId}`] = 0;
      });

      months.push(dataPoint);
    }

    entities.forEach((entity) => {
      entity.sessions.forEach((session) => {
        const sessionMonth = session.date.getMonth();
        const sessionYear = session.date.getFullYear();

        const monthData = months.find((m) => m.month === sessionMonth && m.year === sessionYear);
        if (monthData) {
          const key = `entity_${entity.entityId}`;
          monthData[key] = (monthData[key] as number) + 1;
        }
      });
    });

    return months;
  }, [entities, monthLabels, viewEndDate]);

  const hasData = entities.some((e) => e.sessions.length > 0);

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ dataKey: string; fill: string; value: number }>;
    label?: string;
  }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-gray-900 mb-2">{label}</p>
        {payload.map((entry, index) => {
          const entity = entities.find((e) => `entity_${e.entityId}` === entry.dataKey);
          return (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.fill }} />
              <span className="text-gray-600">{entity?.entityName}:</span>
              <span className="font-medium">{entry.value}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <SessionsPerMonthChartBase
      data={chartData}
      hasData={hasData}
      periodText={periodText}
      canGoNext={canGoNext}
      goToPrevMonth={goToPrevMonth}
      goToNextMonth={goToNextMonth}
      goToPrevYear={goToPrevYear}
      goToNextYear={goToNextYear}
    >
      <Tooltip content={<CustomTooltip />} />
      <Legend
        wrapperStyle={{ paddingTop: "10px" }}
        formatter={(value: string) => {
          const entityId = value.replace("entity_", "");
          const entity = entities.find((e) => String(e.entityId) === entityId);
          return <span className="text-sm text-gray-700">{entity?.entityName || value}</span>;
        }}
      />
      {entities.map((entity) => (
        <Bar
          key={entity.entityId}
          dataKey={`entity_${entity.entityId}`}
          fill={entity.color}
          radius={[4, 4, 0, 0]}
          maxBarSize={30}
        />
      ))}
    </SessionsPerMonthChartBase>
  );
};

export default SessionsPerMonthComparisonChart;
